import { useState } from 'react'
import { api } from '../../api'
import Modal from '../ui/Modal'
import { useTheme } from '../../theme'
import { avecAlpha } from '../../lib/couleurs'

/**
 * Feuille de recharge du wallet (ADR-058 §7, casquette client uniquement — Q2 Enzo) :
 * choix du montant (chips + saisie libre, bornes 1-500 €) puis POST /wallet/recharge.
 * Le Payment Element ne se monte PAS ici : le formulaire carte est long et une
 * feuille le coupe sur mobile (retour terrain v0.73.1) — la feuille rend la main
 * via `onReady(paiement)` et la page Wallet affiche le paiement en pleine page,
 * comme le checkout Paniers (meme cycle de vie Stripe, eprouve).
 */

const ORANGE = 'rgb(var(--c-primary))'
const VERT = 'rgb(var(--c-green))'
const PRESETS = [10, 20, 50]
const MIN = 1
const MAX = 500

function RechargeSheet({ open, onClose, onReady }) {
  const { isWeb3 } = useTheme()
  const [montant, setMontant] = useState(20)
  const [saisie, setSaisie] = useState('') // champ libre (prioritaire si rempli)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')

  const ts = 'text-ink/60'
  const montantChoisi = saisie !== '' ? Number(saisie.replace(',', '.')) : montant
  const montantValide = Number.isFinite(montantChoisi) && montantChoisi >= MIN && montantChoisi <= MAX

  const reset = () => {
    setSaisie('')
    setErr('')
    setLoading(false)
  }

  const close = () => {
    reset()
    onClose()
  }

  const continuer = async () => {
    setErr('')
    setLoading(true)
    try {
      const res = await api.createWalletRecharge(Math.round(montantChoisi * 100) / 100)
      if (!res?.clientSecret) throw new Error('Paiement indisponible pour le moment.')
      reset()
      onReady(res) // la page Wallet prend le relais (paiement pleine page)
    } catch (e) {
      setErr(e.message || 'La recharge n’a pas pu etre initialisee.')
      setLoading(false)
    }
  }

  return (
    <Modal open={open} onClose={close} title="Recharger mon wallet" variant="sheet">
      <div>
          <p className={`text-[12.5px] leading-relaxed mb-4 ${ts}`}>
            Dépose de l'argent sur ton solde particulier — il servira à payer tes commandes Paniers.
          </p>
          <div className="flex gap-2 mb-3">
            {PRESETS.map((p) => {
              const active = saisie === '' && montant === p
              return (
                <button
                  key={p}
                  onClick={() => { setMontant(p); setSaisie('') }}
                  className="flex-1 py-3 rounded-2xl text-[15px] font-extrabold"
                  style={{
                    background: active ? VERT : 'rgba(7,62,61,0.06)',
                    color: active ? 'rgb(var(--c-creme))' : VERT,
                    border: active ? 'none' : '1px solid rgba(7,62,61,0.12)',
                  }}
                >
                  {p} €
                </button>
              )
            })}
          </div>
          <label htmlFor="recharge-libre" className={`block text-[11px] font-semibold mb-1.5 ${ts}`}>
            Ou un autre montant ({MIN}–{MAX} €)
          </label>
          <input
            id="recharge-libre"
            type="text"
            inputMode="decimal"
            placeholder="Ex. 35"
            value={saisie}
            onChange={(e) => setSaisie(e.target.value.replace(/[^0-9.,]/g, ''))}
            className="w-full px-3.5 py-3 rounded-xl border text-[16px] outline-none bg-creme border-[#ede4db] text-ink focus:border-primary"
          />
          {err && <p className="text-[12.5px] mt-2.5 font-semibold" style={{ color: '#d4503a' }}>{err}</p>}
          <button
            onClick={continuer}
            disabled={!montantValide || loading}
            className="mt-4 w-full py-4 rounded-full text-[15px] font-extrabold disabled:opacity-60"
            style={{ background: ORANGE, color: VERT, boxShadow: `0 14px 32px ${avecAlpha(ORANGE, 0.33)}` }}
          >
            {loading
              ? 'Préparation…'
              : montantValide
                ? `Continuer — ${montantChoisi.toFixed(2).replace('.', ',')} €`
                : 'Choisis un montant'}
          </button>
      </div>
    </Modal>
  )
}

export default RechargeSheet
